import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { DobAlert, DobButton, DobIcon, DobInput } from '@dedalusdiit/dob-components';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import Style from './WorklistToDashboardDialog.module.scss';
import { IWorklistToDashboardDialogViewStore } from './WorklistToDashboardDialogViewStore';
import { ICurrentWorklistDashboardHelper, MAX_ABBREVIATION_LENGTH } from '@/Pages/GenericWorklist/Stores/CurrentWorklistDashboardHelper';

export interface WorklistToDashboardDialogProps {
    currentWorklistDashboardHelper: ICurrentWorklistDashboardHelper;
    store: IWorklistToDashboardDialogViewStore;
}

const {
    dialogContent: dialogContentStyle,
    thresholdContainer: thresholdContainerStyle,
    thresholdContainerHigh: thresholdContainerHighStyle,
    thresholdContainerMedium: thresholdContainerMediumStyle,
    worklistInfo: worklistInfoStyle,
    worklistInfoTitle: worklistInfoTitleStyle,
} = Style;

const WorklistToDashboardDialog = (props: Readonly<WorklistToDashboardDialogProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store, currentWorklistDashboardHelper } = props;
    const {
        abbreviation,
        addButtonState,
        commitFormInput,
        currentWorklistTitle,
        deleteButtonState,
        highThresholdString,
        isDialogOpen,
        mediumThresholdString,
        renderedErrors,
        saveButtonState,
        delete: deleteWorklistFromDashboard,
        save,
        setAbbreviation,
        setHighThreshold,
        setMediumThreshold,
        toggleIsDialogOpen,
    } = store;

    const validationErrorAlert = useMemo(() => {
        const values = Object.values(renderedErrors);
        const errorMessages = values
            .flatMap((errorList) => errorList ?? []);

        if (!errorMessages.length) {
            return;
        }

        return (
            <DobAlert type="error">
                {errorMessages.map((error) => (
                    <span key={error}>
                        {error}
                        <br />
                    </span>
                ))}
            </DobAlert>
        );
    }, [renderedErrors]);

    const onAbbreviationValueChange = (value: string) => {
        setAbbreviation(value);
    };

    const onCancelClickHandler = () => {
        toggleIsDialogOpen(false);
    };

    const onDeleteClickHandler = () => {
        if (deleteButtonState !== 'enabled') {
            throw new Error(`Not possible to delete current worklist from the dashboard`);
        }

        deleteWorklistFromDashboard()
            .catch(() => console.warn(`Failed to delete the current worklist from the dashboard`));
    };

    const onHighThresholdValueChange = (value: string) => {
        setHighThreshold(value);
    };

    const onMediumThresholdValueChange = (value: string) => {
        setMediumThreshold(value);
    };

    const onAddClickHandler = () => {
        if (addButtonState !== 'enabled') {
            throw new Error(`Not possible to add current worklist to the dashboard`);
        }

        save()
            .catch(() => console.warn(`Failed to add the current worklist to the dashboard`));
    };

    const onSaveClickHandler = () => {
        if (saveButtonState !== 'enabled') {
            throw new Error(`Not possible to save current worklist from the dashboard`);
        }

        save()
            .catch(() => console.warn(`Failed to save the current worklist to the dashboard`));
    };

    return (
        <Dialog open={isDialogOpen} width="small">
            <DialogTitle>
                <span>{t(currentWorklistDashboardHelper.dialogActionTitleKey)}</span>
            </DialogTitle>
            <DialogContent>
                <div className={dialogContentStyle}>
                    <div className={worklistInfoStyle}>
                        <DobIcon
                            name="list-task"
                            size="2rem"
                        />
                        <span className={worklistInfoTitleStyle}>{currentWorklistTitle}</span>
                    </div>
                    <DobInput
                        direction="vertical"
                        error={!!renderedErrors.abbreviation.length}
                        label={t('Abbreviation')}
                        minLength={1}
                        maxLength={MAX_ABBREVIATION_LENGTH}
                        onBlur={() => commitFormInput('abbreviation')}
                        onValueChange={onAbbreviationValueChange}
                        type="normal"
                        value={abbreviation}
                    />
                    <div className={classNames(thresholdContainerStyle, thresholdContainerMediumStyle)}>
                        <DobInput
                            direction="vertical"
                            error={!!renderedErrors.mediumThreshold.length}
                            label={t('MediumThreshold')}
                            min={1}
                            onBlur={() => commitFormInput('mediumThreshold')}
                            onValueChange={onMediumThresholdValueChange}
                            type="number"
                            value={mediumThresholdString}
                        />
                    </div>
                    <div className={classNames(thresholdContainerStyle, thresholdContainerHighStyle)}>
                        <DobInput
                            direction="vertical"
                            error={!!renderedErrors.highThreshold.length}
                            label={t('HighThreshold')}
                            min={1}
                            onBlur={() => commitFormInput('highThreshold')}
                            onValueChange={onHighThresholdValueChange}
                            type="number"
                            value={highThresholdString}
                        />
                    </div>
                    {validationErrorAlert}
                </div>
            </DialogContent>
            <DialogButtons>
                <DobButton color="silent" onClick={onCancelClickHandler}> {t('Cancel')}</DobButton>
                <>{ addButtonState !== 'hidden' && (<DobButton color="primary" disabled={['disabled', 'loading'].includes(addButtonState)} loading={addButtonState === 'loading'} onClick={onAddClickHandler}>{t('Add')}</DobButton>) }</>
                <>{ deleteButtonState !== 'hidden' && (<DobButton color="danger" disabled={['disabled', 'loading'].includes(deleteButtonState)} loading={deleteButtonState === 'loading'} onClick={onDeleteClickHandler}>{t('Remove')}</DobButton>) }</>
                <>{ saveButtonState !== 'hidden' && (<DobButton color="primary" disabled={['disabled', 'loading'].includes(saveButtonState)} loading={saveButtonState === 'loading'} onClick={onSaveClickHandler}>{t('Save')}</DobButton>) }</>
            </DialogButtons>
        </Dialog>
    );
};

export default observer(WorklistToDashboardDialog);
